import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Tabs,
  Descriptions,
  Timeline,
  Table,
  Upload,
  Button,
  Tag,
  Space,
  Spin,
  message,
  Modal,
  Select,
} from "antd";
import {
  UploadOutlined,
  RobotOutlined,
  FileTextOutlined,
  DeleteOutlined,
  PrinterOutlined,
  EyeOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  SyncOutlined,
  TableOutlined,
} from "@ant-design/icons";
import {
  caseAPI,
  partyAPI,
  eventAPI,
  documentAPI,
  evidenceAPI,
  aiAPI,
  db1995API,
  matriculaAPI,
} from "../services/api";
import { Case, Party, Event, Document, Evidence } from "../types";
import MatriculaPlanillaTable from "../components/MatriculaPlanillaTable";
import dayjs from "dayjs";

const { TabPane } = Tabs;

function getMatriculasPrintHtml(report: {
  n_matriculas?: number;
  adquirente?: string;
  nota?: string;
  por_transferencia?: Record<string, number>;
  items?: Array<{
    transferencia?: string;
    inmueble?: string;
    descripcion?: string;
    matricula?: string;
    superficie_m2?: string;
  }>;
}): string {
  const items = report.items || [];
  const rows = items
    .map(
      (it, i) =>
        `<tr>
          <td>${i + 1}</td>
          <td>${escapePrint(it.transferencia || "")}</td>
          <td>${escapePrint(it.inmueble || "")}</td>
          <td>${escapePrint(it.descripcion || "")}</td>
          <td class="mat">${escapePrint(it.matricula || "")}</td>
          <td class="num">${escapePrint(it.superficie_m2 || "")}</td>
        </tr>`
    )
    .join("");
  const porT = report.por_transferencia
    ? Object.entries(report.por_transferencia)
        .map(([k, v]) => `<li><strong>${escapePrint(k)}</strong>: ${v} matrícula(s)</li>`)
        .join("")
    : "";
  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <title>Matrículas adquiridas — Gabriela Camacho de Freitas</title>
  <style>
    @page { size: A4; margin: 1.5cm; }
    body { font-family: "Times New Roman", Times, serif; font-size: 11pt; color: #111; }
    h1 { font-size: 16pt; color: #1F3D2B; margin: 0 0 4px 0; }
    h2 { font-size: 12pt; color: #1F3D2B; margin: 18px 0 8px 0; }
    .meta { color: #555; font-size: 10pt; margin-bottom: 16px; }
    .note { background: #fff8e6; border: 1px solid #e6d9a8; padding: 10px 12px; margin: 12px 0; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th, td { border: 1px solid #ccc; padding: 5px 6px; vertical-align: top; }
    th { background: #1F3D2B; color: #fff; font-size: 10pt; text-align: left; }
    tr:nth-child(even) td { background: #f5f8f6; }
    td.mat { font-weight: bold; white-space: nowrap; }
    td.num { text-align: right; white-space: nowrap; }
    .actions { margin: 16px 0; }
    .actions button {
      background: #1F3D2B; color: #fff; border: 0; padding: 10px 18px;
      font-size: 12pt; cursor: pointer; margin-right: 8px;
    }
    @media print {
      .actions { display: none; }
      th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="actions">
    <button onclick="window.print()">Imprimir / Guardar PDF</button>
    <button onclick="window.close()" style="background:#666">Cerrar</button>
  </div>
  <h1>PROPIEDADES COMPRADAS — LISTADO DE MATRÍCULAS</h1>
  <p class="meta">
    Adquirente: <strong>${escapePrint(report.adquirente || "CAMACHO DE FREITAS GABRIELA")}</strong><br/>
    Caso Comarca 9 · 7 transferencias 2021 · Generado: ${new Date().toLocaleString("es-ES")}<br/>
    Total: <strong>${report.n_matriculas ?? items.length}</strong> matrículas (una fila = una matrícula)
  </p>
  <div class="note">${escapePrint(report.nota || "Listado para departamento legal. Una matrícula por fila, sin duplicados.")}</div>
  ${porT ? `<h2>Resumen por escritura</h2><ul>${porT}</ul>` : ""}
  <h2>Listado completo de matrículas</h2>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Transferencia</th>
        <th>Inmueble</th>
        <th>Descripción / lote</th>
        <th>Matrícula</th>
        <th>Superficie m²</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;
}

function escapePrint(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function openLegalInformeWindow(caseId: number, documentId?: number) {
  const url = documentAPI.informeLegalHtmlUrl(caseId, documentId);
  const w = window.open(url, "_blank");
  if (!w) {
    message.warning("Permite ventanas emergentes para ver el informe jurídico");
  }
}

function openMatriculasPrintWindow(report: any) {
  const w = window.open("", "_blank");
  if (!w) {
    message.warning("Permite ventanas emergentes para ver/imprimir las matrículas");
    return;
  }
  w.document.write(getMatriculasPrintHtml(report));
  w.document.close();
  w.focus();
}

const CaseView: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const [case_, setCase] = useState<Case | null>(null);
  const [parties, setParties] = useState<Party[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [documentAnalysis, setDocumentAnalysis] = useState<any>(null);
  const [analyzingDocument, setAnalyzingDocument] = useState<number | null>(
    null
  );
  const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
  const [documentAnalyses, setDocumentAnalyses] = useState<
    Map<number, boolean>
  >(new Map()); // Track which documents have saved analyses
  const [selectedDocuments, setSelectedDocuments] = useState<React.Key[]>([]); // Selected document IDs for combined analysis
  const [loading, setLoading] = useState(true);
  const [copyingDocId, setCopyingDocId] = useState<number | null>(null);
  const [copyingAllPdfs, setCopyingAllPdfs] = useState(false);
  const [ocrViewVisible, setOcrViewVisible] = useState(false);
  const [ocrViewTitle, setOcrViewTitle] = useState("");
  const [ocrViewText, setOcrViewText] = useState("");
  const [ocrViewLoading, setOcrViewLoading] = useState(false);
  const [ocrViewMeta, setOcrViewMeta] = useState("");
  const [matriculas, setMatriculas] = useState<any[]>([]);
  const [matriculasLoading, setMatriculasLoading] = useState(false);
  const [extractingMatriculas, setExtractingMatriculas] = useState(false);

  useEffect(() => {
    if (caseId) {
      fetchCaseData(parseInt(caseId));
    }
  }, [caseId]);

  const fetchCaseData = async (id: number) => {
    try {
      const [caseData, partiesData, eventsData, docsData, evidenceData] =
        await Promise.all([
          caseAPI.getById(id),
          partyAPI.getByCaseId(id),
          eventAPI.getByCaseId(id),
          documentAPI.getByCaseId(id),
          evidenceAPI.getByCaseId(id),
        ]);

      setCase(caseData);
      setParties(partiesData);
      setEvents(eventsData);
      setDocuments(docsData);
      setEvidence(evidenceData);

      setMatriculasLoading(true);
      try {
        const mats = await matriculaAPI.listByCase(id);
        setMatriculas(Array.isArray(mats) ? mats : []);
      } catch {
        setMatriculas([]);
      } finally {
        setMatriculasLoading(false);
      }

      // Check which documents have saved analyses
      const analysesMap = new Map<number, boolean>();
      for (const doc of docsData) {
        try {
          const saved = await documentAPI.getSavedAnalysis(doc.document_id);
          analysesMap.set(doc.document_id, !!(saved && saved.found));
        } catch {
          analysesMap.set(doc.document_id, false);
        }
      }
      setDocumentAnalyses(analysesMap);
    } catch (error) {
      console.error("Error fetching case data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAIAnalysis = async () => {
    if (!caseId) return;
    try {
      message.loading({
        content: "Ejecutando análisis de IA...",
        key: "ai-analysis",
        duration: 0,
      });
      const [risk, omissions, precedents] = await Promise.all([
        aiAPI.getRiskAnalysis(parseInt(caseId)),
        aiAPI.getOmissions(parseInt(caseId)),
        aiAPI.getPrecedents(parseInt(caseId)),
      ]);
      setAiAnalysis({ risk, omissions, precedents });
      message.success({ content: "Análisis completado", key: "ai-analysis" });
    } catch (error: any) {
      console.error("Error running AI analysis:", error);
      const errorMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        "Error desconocido al ejecutar el análisis";
      if (error?.response?.status === 429) {
        const detail = error?.response?.data?.detail || "";
        if (detail.includes("quota") || detail.includes("billing")) {
          message.error({
            content:
              "Cuota de OpenAI agotada. Por favor, verifica tu plan y facturación en OpenAI, o configura Ollama para usar IA local gratuita.",
            key: "ai-analysis",
            duration: 10,
          });
        } else {
          message.error({
            content:
              "Demasiadas solicitudes. Por favor, espera unos momentos y vuelve a intentar.",
            key: "ai-analysis",
            duration: 5,
          });
        }
      } else {
        message.error({
          content: `Error: ${errorMsg}`,
          key: "ai-analysis",
          duration: 5,
        });
      }
    }
  };

  const handleCombinedAnalysis = async (documentIds: React.Key[]) => {
    if (documentIds.length === 0) {
      message.warning("Por favor, selecciona al menos un documento");
      return;
    }

    setAnalyzingDocument(-1); // Use -1 to indicate combined analysis
    try {
      const docTitles = documents
        .filter((d) =>
          documentIds.some((k) => Number(k) === d.document_id)
        )
        .map((d) => d.title)
        .join(", ");

      message.loading({
        content: `Analizando ${documentIds.length} documento(s) combinado(s)...`,
        key: "combined-analysis",
        duration: 0,
      });

      const analysis = await documentAPI.analyzeCombined(
        documentIds.map((id) => Number(id))
      );

      if (analysis.error) {
        if (
          analysis.error_type === "quota_exceeded" ||
          analysis.error_type === "rate_limit"
        ) {
          message.error({
            content:
              analysis.error ||
              "Cuota de API agotada. Por favor, verifica tu configuración.",
            key: "combined-analysis",
            duration: 5,
          });
        } else {
          message.error({
            content: analysis.error || "Error al analizar los documentos",
            key: "combined-analysis",
            duration: 5,
          });
        }
        setAnalyzingDocument(null);
        return;
      }

      setDocumentAnalysis({
        documentId: -1,
        documentTitle: `Análisis Combinado: ${docTitles}`,
        analysis,
      });
      setAnalysisModalVisible(true);
      setSelectedDocuments([]); // Clear selection after analysis

      message.success({
        content: analysis?.reporte_compra_7_docs?.ok
          ? `Análisis combinado · Reporte 7 transferencias: ${analysis.reporte_compra_7_docs.n_matriculas} matrículas`
          : "Análisis combinado completado",
        key: "combined-analysis",
      });
    } catch (error: any) {
      console.error("Error analyzing combined documents:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        "Error desconocido";
      if (error?.response?.status === 429) {
        message.error({
          content:
            "Demasiadas solicitudes. Por favor, espera unos momentos y vuelve a intentar.",
          key: "combined-analysis",
          duration: 5,
        });
      } else {
        message.error({
          content: `Error: ${errorMsg}`,
          key: "combined-analysis",
          duration: 5,
        });
      }
    } finally {
      setAnalyzingDocument(null);
    }
  };

  const handleDocumentAnalysis = async (
    documentId: number,
    documentTitle: string,
    useSaved: boolean = false
  ) => {
    setAnalyzingDocument(documentId);
    try {
      let analysis;

      if (useSaved) {
        // Load saved analysis
        message.loading({
          content: `Cargando análisis guardado de ${documentTitle}...`,
          key: "doc-analysis",
          duration: 0,
        });
        const saved = await documentAPI.getSavedAnalysis(documentId);
        if (!saved?.found || !saved.results) {
          throw new Error("No hay análisis guardado para este documento");
        }
        analysis = saved.results;
        analysis.analysis_id = saved.analysis_id;
        analysis.saved_at = saved.analysis_date;
      } else {
        // Run new analysis
        message.loading({
          content: `Analizando ${documentTitle}...`,
          key: "doc-analysis",
          duration: 0,
        });
        analysis = await documentAPI.analyze(documentId);
      }

      if (analysis.error) {
        if (
          analysis.error_type === "quota_exceeded" ||
          analysis.error_type === "rate_limit"
        ) {
          message.error({
            content:
              analysis.error ||
              "Cuota de API agotada. Por favor, verifica tu configuración.",
            key: "doc-analysis",
            duration: 5,
          });
        } else {
          message.error({
            content: analysis.error || "Error al analizar el documento",
            key: "doc-analysis",
            duration: 5,
          });
        }
        setAnalyzingDocument(null);
        return;
      }

      setDocumentAnalysis({ documentId, documentTitle, analysis });
      setAnalysisModalVisible(true);

      // Caso 9: abrir informe jurídico (y matrículas) para el dpto. legal
      if (String(caseId) === "9") {
        try {
          let report = analysis?.reporte_compra_7_docs;
          if (!report?.ok) {
            report = await documentAPI.generateBoughtPropertiesReport(9);
            analysis.reporte_compra_7_docs = report;
            setDocumentAnalysis({ documentId, documentTitle, analysis: { ...analysis } });
          }
          const isTestimonio =
            /TESTIMONIO|PODER/i.test(documentTitle) ||
            analysis?.informe_tipo === "legal_precautela" ||
            analysis?.formato === "informe_juridico";
          if (isTestimonio) {
            openLegalInformeWindow(9, documentId);
          } else if (report?.ok && report.items?.length) {
            openMatriculasPrintWindow(report);
          }
        } catch (e) {
          console.warn("No se pudo abrir informe jurídico / matrículas", e);
        }
      }

      // Update saved analyses map
      if (analysis.analysis_id) {
        setDocumentAnalyses((prev) => new Map(prev).set(documentId, true));
      }

      message.success({
        content: useSaved
          ? "Análisis cargado"
          : analysis?.reporte_compra_7_docs?.ok
            ? `Análisis guardado · Reporte 7 transferencias: ${analysis.reporte_compra_7_docs.n_matriculas} matrículas (ventana de impresión abierta)`
            : "Análisis completado y guardado",
        key: "doc-analysis",
      });
    } catch (error: any) {
      console.error("Error analyzing document:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        "Error desconocido";
      if (error?.response?.status === 429) {
        message.error({
          content:
            "Demasiadas solicitudes. Por favor, espera unos momentos y vuelve a intentar.",
          key: "doc-analysis",
          duration: 5,
        });
      } else if (error?.response?.status === 404 && useSaved) {
        message.info({
          content: "No hay análisis guardado. Ejecutando nuevo análisis...",
          key: "doc-analysis",
          duration: 3,
        });
        // Try running new analysis instead
        await handleDocumentAnalysis(documentId, documentTitle, false);
        return;
      } else {
        message.error({
          content: `Error: ${errorMsg}`,
          key: "doc-analysis",
          duration: 5,
        });
      }
    } finally {
      setAnalyzingDocument(null);
    }
  };

  if (loading) return <Spin size="large" />;
  if (!case_) return <div>Caso no encontrado</div>;

  return (
    <div>
      <Card
        title={
          <span style={{ fontSize: "24px" }}>📁 Caso {case_.case_number}</span>
        }
        extra={
          <Space>
            <Tag color={case_.priority === "high" ? "red" : "blue"}>
              {case_.priority?.toUpperCase()}
            </Tag>
            <Tag color={case_.status === "active" ? "green" : "default"}>
              {case_.status?.toUpperCase()}
            </Tag>
          </Space>
        }
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Tipo">{case_.case_type}</Descriptions.Item>
          <Descriptions.Item label="Jurisdicción">
            {case_.jurisdiction}
          </Descriptions.Item>
          <Descriptions.Item label="Fecha de Presentación">
            {dayjs(case_.filing_date).format("DD/MM/YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Estado">{case_.status}</Descriptions.Item>
          <Descriptions.Item label="Resumen" span={2}>
            {case_.summary}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs defaultActiveKey="1" style={{ marginTop: 24 }}>
        <TabPane tab="👥 Partes" key="1">
          <Card>
            {parties.map((party) => (
              <Card.Grid key={party.party_id} style={{ width: "50%" }}>
                <p>
                  <strong>Nombre:</strong> {party.full_name}
                </p>
                <p>
                  <strong>Tipo:</strong> <Tag>{party.party_type}</Tag>
                </p>
                {party.id_number && (
                  <p>
                    <strong>C.I.:</strong> {party.id_number}
                  </p>
                )}
                {party.role && (
                  <p>
                    <strong>Rol:</strong> {party.role}
                  </p>
                )}
              </Card.Grid>
            ))}
          </Card>
        </TabPane>

        <TabPane tab="📅 Cronología" key="2">
          <Card>
            <Timeline
              items={events
                .sort(
                  (a, b) =>
                    new Date(a.event_date).getTime() -
                    new Date(b.event_date).getTime()
                )
                .map((event) => ({
                  children: (
                    <div>
                      <div style={{ fontWeight: "bold" }}>
                        {dayjs(event.event_date).format("DD/MM/YYYY")} -{" "}
                        {event.event_type}
                      </div>
                      <div>{event.description}</div>
                      {event.outcome && (
                        <div style={{ fontStyle: "italic" }}>
                          Resultado: {event.outcome}
                        </div>
                      )}
                    </div>
                  ),
                }))}
            />
          </Card>
        </TabPane>

        <TabPane tab="📄 Documentos" key="3">
          <Card
            extra={
              <Space wrap>
                <Button
                  icon={<DatabaseOutlined />}
                  loading={copyingAllPdfs}
                  onClick={async () => {
                    if (!caseId) return;
                    setCopyingAllPdfs(true);
                    try {
                      const res = await documentAPI.copyCasePdfsToDb1995(
                        parseInt(caseId)
                      );
                      message.success(
                        `db1995 actualizado: ${res.new_rows} nuevos, ${res.updated_rows} actualizados`
                      );
                      if (caseId) fetchCaseData(parseInt(caseId));
                    } catch (e: any) {
                      message.error(
                        e?.response?.data?.detail ||
                          "Error al copiar a db1995"
                      );
                    } finally {
                      setCopyingAllPdfs(false);
                    }
                  }}
                >
                  Copiar todo a db1995
                </Button>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={async () => {
                    if (!caseId) return;
                    try {
                      message.loading({
                        content: "Cargando último análisis combinado guardado...",
                        key: "combined-load",
                        duration: 0,
                      });
                      const saved = await aiAPI.getCombinedAnalysis(parseInt(caseId));
                      setDocumentAnalysis({
                        documentId: -1,
                        documentTitle: `Análisis Combinado (${new Date(saved.analysis_date).toLocaleDateString("es-ES")})`,
                        analysis: { ...saved.results, saved_at: saved.analysis_date },
                      });
                      setAnalysisModalVisible(true);
                      message.success({
                        content: "Análisis cargado",
                        key: "combined-load",
                      });
                    } catch (e: any) {
                      if (e?.response?.status === 404) {
                        message.info({
                          content: "No hay análisis combinado guardado. Ejecuta uno primero.",
                          key: "combined-load",
                        });
                      } else {
                        message.error({
                          content: e?.response?.data?.detail || "Error al cargar",
                          key: "combined-load",
                        });
                      }
                    }
                  }}
                >
                  Ver último análisis
                </Button>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={() => {
                    if (!caseId) return;
                    window.open(
                      aiAPI.getOcrExportUrl(parseInt(caseId)),
                      "_blank"
                    );
                    message.success("Descargando OCR en formato Markdown...");
                  }}
                >
                  Descargar OCR (MD)
                </Button>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={async () => {
                    if (!caseId) return;
                    try {
                      message.loading({
                        content: "Extrayendo autoridades y agregando a MAC...",
                        key: "mac-extract",
                        duration: 0,
                      });
                      const res = await aiAPI.extractAuthoritiesToMac(parseInt(caseId));
                      message.success({
                        content: `Agregadas ${res.added} autoridades a MAC (mac.5bc.us)`,
                        key: "mac-extract",
                        duration: 5,
                      });
                    } catch (e: any) {
                      message.error({
                        content: e?.response?.data?.detail || "Error al extraer a MAC",
                        key: "mac-extract",
                      });
                    }
                  }}
                >
                  Extraer a MAC
                </Button>
                {String(caseId) === "9" && (
                  <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={() => openLegalInformeWindow(9)}
                  >
                    Informe jurídico (imprimir)
                  </Button>
                )}
                {selectedDocuments.length > 0 && (
                  <Button
                    type="primary"
                    icon={<RobotOutlined />}
                    onClick={() => handleCombinedAnalysis(selectedDocuments)}
                    loading={analyzingDocument === -1}
                  >
                    Analizar {selectedDocuments.length} Documento
                    {selectedDocuments.length > 1 ? "s" : ""} Seleccionado
                    {selectedDocuments.length > 1 ? "s" : ""}
                  </Button>
                )}
                <Button
                  icon={<TableOutlined />}
                  type="primary"
                  ghost
                  loading={extractingMatriculas}
                  onClick={async () => {
                    if (!caseId) return;
                    setExtractingMatriculas(true);
                    try {
                      const res = await matriculaAPI.extractCase(parseInt(caseId));
                      message.success(
                        `Planilla actualizada: ${res.upserted} matrículas de ${res.scanned} documentos` +
                          (res.skipped_not_folio
                            ? ` (${res.skipped_not_folio} no folio)`
                            : "")
                      );
                      const mats = await matriculaAPI.listByCase(parseInt(caseId));
                      setMatriculas(Array.isArray(mats) ? mats : []);
                    } catch (e: any) {
                      message.error(
                        e?.response?.data?.detail ||
                          "Error al actualizar la planilla"
                      );
                    } finally {
                      setExtractingMatriculas(false);
                    }
                  }}
                >
                  Actualizar planilla
                </Button>
                <Upload
                  name="file"
                  multiple
                  action={`${process.env.REACT_APP_API_URL || "https://comarca.5bc.us/api"}/documents/upload`}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tif,.tiff,.gif,.bmp,.mp3,.wav,.ogg,.opus,.m4a,.mp4,.webm,.mov,.avi,.mkv,audio/*,video/*,image/*"
                  data={{
                    case_id: parseInt(caseId || "0"),
                    document_type: "documento",
                  }}
                  onChange={(info) => {
                    const { file, fileList } = info;
                    const active = fileList.filter(
                      (f) => f.originFileObj != null && f.status !== "removed"
                    );
                    const uploading = active.some(
                      (f) => f.status === "uploading"
                    );
                    const doneOk = active.filter((f) => f.status === "done");
                    const doneErr = active.filter((f) => f.status === "error");
                    const allSettled =
                      active.length > 0 &&
                      !uploading &&
                      doneOk.length + doneErr.length === active.length;

                    if (file.status === "error") {
                      const resp = file.response as
                        | { detail?: string }
                        | string
                        | undefined;
                      const errMsg = (
                        (file.error as { message?: string } | undefined)
                          ?.message || ""
                      ).toLowerCase();
                      const detail =
                        (typeof resp === "object" && resp?.detail) ||
                        (typeof resp === "string" ? resp : "") ||
                        (file.error as { message?: string } | undefined)
                          ?.message ||
                        "falló al subir";
                      const sizeHint =
                        (file.size || 0) > 4 * 1024 * 1024 ||
                        errMsg.includes("timeout") ||
                        errMsg.includes("network")
                          ? " (PDFs grandes: reintente; el OCR ya no bloquea la subida)"
                          : "";
                      message.error(`${file.name}: ${detail}${sizeHint}`);
                    }

                    if (allSettled) {
                      if (doneOk.length > 0) {
                        const tiffConverted = doneOk.filter((f) => {
                          const r = f.response as
                            | { converted_from_tiff?: boolean }
                            | undefined;
                          return r?.converted_from_tiff;
                        }).length;
                        const deferred = doneOk.filter((f) => {
                          const r = f.response as
                            | { analysis?: { deferred_analysis?: boolean } }
                            | undefined;
                          return r?.analysis?.deferred_analysis;
                        }).length;
                        const n = doneOk.length;
                        let okMsg =
                          n === 1
                            ? `${doneOk[0].name} subido correctamente`
                            : `${n} archivos subidos correctamente`;
                        if (tiffConverted > 0) {
                          okMsg +=
                            tiffConverted === 1
                              ? " (1 TIFF → PDF)"
                              : ` (${tiffConverted} TIFF → PDF)`;
                        }
                        if (deferred > 0) {
                          okMsg +=
                            " — OCR diferido: use Analizar en cada documento";
                        }
                        message.success(okMsg);
                      }
                      if (caseId) fetchCaseData(parseInt(caseId));
                    }
                  }}
                >
                  <Button icon={<UploadOutlined />}>
                    Subir documentos (varios)
                  </Button>
                </Upload>
              </Space>
            }
          >
            <Table
              dataSource={documents}
              rowKey="document_id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} documentos`,
              }}
              rowSelection={{
                selectedRowKeys: selectedDocuments,
                preserveSelectedRowKeys: true,
                onChange: (selectedRowKeys) => {
                  setSelectedDocuments(selectedRowKeys);
                },
              }}
              columns={[
                {
                  title: "Título",
                  dataIndex: "title",
                  key: "title",
                  render: (text: string, record: any) => (
                    <a
                      href={`https://comarca.5bc.us/api/documents/download/${record.document_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {text}
                    </a>
                  ),
                },
                {
                  title: "Tipo",
                  dataIndex: "document_type",
                  key: "document_type",
                },
                {
                  title: "Vinculado a",
                  dataIndex: "event_id",
                  key: "event_id",
                  render: (eventId: number | null, record: any) => (
                    <Select
                      placeholder="Vincular a evento"
                      allowClear
                      value={eventId || undefined}
                      style={{ width: 200 }}
                      onChange={async (val) => {
                        try {
                          await documentAPI.update(record.document_id, {
                            event_id: val ?? null,
                          });
                          message.success("Documento vinculado");
                          if (caseId) fetchCaseData(parseInt(caseId));
                        } catch {
                          message.error("Error al vincular");
                        }
                      }}
                      options={events.map((e) => ({
                        value: e.event_id,
                        label: `${dayjs(e.event_date).format("DD/MM/YYYY")} - ${e.event_type}`,
                      }))}
                    />
                  ),
                },
                {
                  title: "Fecha",
                  dataIndex: "upload_date",
                  key: "upload_date",
                  render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
                },
                {
                  title: "Estado",
                  dataIndex: "status",
                  key: "status",
                  render: (status: string) => <Tag>{status}</Tag>,
                },
                {
                  title: "OCR / DB1995",
                  key: "ocr",
                  width: 200,
                  render: (_: any, record: any) => {
                    const chars = (record.content_text || "").trim().length;
                    return (
                      <Space direction="vertical" size={0}>
                        <Tag color={chars > 0 ? "green" : "default"}>
                          {chars > 0 ? `${chars.toLocaleString()} chars` : "Sin texto"}
                        </Tag>
                        <Button
                          type="link"
                          size="small"
                          icon={<FileTextOutlined />}
                          onClick={async () => {
                            setOcrViewTitle(record.title || `Documento #${record.document_id}`);
                            setOcrViewVisible(true);
                            const cached = (record.content_text || "").trim();
                            if (cached) {
                              setOcrViewText(cached);
                              setOcrViewLoading(false);
                              return;
                            }
                            setOcrViewLoading(true);
                            setOcrViewText("");
                            try {
                              const text = await documentAPI.getPlainText(record.document_id);
                              setOcrViewText(
                                text || "No hay texto guardado para este documento."
                              );
                            } catch {
                              setOcrViewText("Error al cargar el texto.");
                            } finally {
                              setOcrViewLoading(false);
                            }
                          }}
                        >
                          Ver texto
                        </Button>
                      </Space>
                    );
                  },
                },
                {
                  title: "Acciones",
                  key: "actions",
                  render: (_: any, record: any) => {
                    const hasSavedAnalysis = documentAnalyses.get(
                      record.document_id
                    );
                    const isTiff = /\.tiff?$/i.test(record.title || "");
                    return (
                      <Space>
                        <Button
                          type="link"
                          size="small"
                          icon={<EyeOutlined />}
                          href={`https://comarca.5bc.us/api/documents/download/${record.document_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          icon={<DatabaseOutlined />}
                          loading={copyingDocId === record.document_id}
                          onClick={async () => {
                            setCopyingDocId(record.document_id);
                            try {
                              const res = await documentAPI.copyToDb1995(
                                record.document_id
                              );
                              const chars =
                                res.plain_text_chars ??
                                (res.plain_text || "").trim().length;
                              message.success(
                                `${record.title} guardado en db1995 (${res.file_type || "archivo"}, ${chars.toLocaleString()} chars)`
                              );
                              setOcrViewTitle(
                                `DB1995 · ${res.original_file_name || record.title}`
                              );
                              setOcrViewMeta(
                                `Tipo: ${res.file_type || "—"} · ID db1995: ${res.id} · ${chars.toLocaleString()} caracteres`
                              );
                              setOcrViewVisible(true);
                              if ((res.plain_text || "").trim()) {
                                setOcrViewText(res.plain_text || "");
                                setOcrViewLoading(false);
                              } else {
                                setOcrViewLoading(true);
                                setOcrViewText("");
                                try {
                                  const fromDb = await db1995API.getByDocument(
                                    record.document_id
                                  );
                                  setOcrViewText(
                                    fromDb.plain_text ||
                                      "No hay texto guardado en db1995 para este archivo."
                                  );
                                  setOcrViewMeta(
                                    `Tipo: ${fromDb.file_type || "—"} · ID db1995: ${fromDb.id} · ${(fromDb.plain_text_chars || 0).toLocaleString()} caracteres`
                                  );
                                } catch {
                                  setOcrViewText(
                                    "Archivo copiado a db1995, pero no hay texto extraíble (p. ej. audio/video)."
                                  );
                                } finally {
                                  setOcrViewLoading(false);
                                }
                              }
                              if (caseId) fetchCaseData(parseInt(caseId));
                            } catch (e: any) {
                              message.error(
                                e?.response?.data?.detail ||
                                  "Error al guardar en db1995"
                              );
                            } finally {
                              setCopyingDocId(null);
                            }
                          }}
                        >
                          db1995
                        </Button>
                        {isTiff && (
                          <Button
                            type="link"
                            size="small"
                            onClick={async () => {
                              try {
                                message.loading({
                                  content: "Convirtiendo TIFF a PDF…",
                                  key: "tiff-pdf",
                                  duration: 0,
                                });
                                await documentAPI.convertTiffToPdf(
                                  record.document_id
                                );
                                message.success({
                                  content: "Documento convertido a PDF",
                                  key: "tiff-pdf",
                                });
                                if (caseId) fetchCaseData(parseInt(caseId));
                              } catch (e: any) {
                                message.error({
                                  content:
                                    e?.response?.data?.detail ||
                                    "Error al convertir",
                                  key: "tiff-pdf",
                                });
                              }
                            }}
                          >
                            TIFF → PDF
                          </Button>
                        )}
                        {hasSavedAnalysis && (
                          <Button
                            type="link"
                            size="small"
                            onClick={() =>
                              handleDocumentAnalysis(
                                record.document_id,
                                record.title,
                                true
                              )
                            }
                            title="Ver análisis guardado"
                          >
                            Ver Análisis
                          </Button>
                        )}
                        <Button
                          type="text"
                          icon={<RobotOutlined />}
                          loading={analyzingDocument === record.document_id}
                          onClick={() =>
                            handleDocumentAnalysis(
                              record.document_id,
                              record.title,
                              false
                            )
                          }
                          title="Obtener asesoramiento legal"
                        >
                          {hasSavedAnalysis ? "Re-analizar" : "Analizar"}
                        </Button>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={async () => {
                            try {
                              await documentAPI.delete(record.document_id);
                              message.success(
                                "Documento eliminado exitosamente"
                              );
                              if (caseId) fetchCaseData(parseInt(caseId));
                            } catch (error) {
                              message.error("Error al eliminar documento");
                              console.error("Error deleting document:", error);
                            }
                          }}
                        />
                      </Space>
                    );
                  },
                },
              ]}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <TableOutlined /> Planilla
            </span>
          }
          key="planilla"
        >
          <Card
            extra={
              <Space wrap>
                <Button
                  icon={<SyncOutlined />}
                  loading={extractingMatriculas}
                  onClick={async () => {
                    if (!caseId) return;
                    setExtractingMatriculas(true);
                    try {
                      const res = await matriculaAPI.extractCase(parseInt(caseId));
                      message.success(
                        `Planilla actualizada: ${res.upserted} matrículas`
                      );
                      const mats = await matriculaAPI.listByCase(parseInt(caseId));
                      setMatriculas(Array.isArray(mats) ? mats : []);
                    } catch (e: any) {
                      message.error(
                        e?.response?.data?.detail || "Error al extraer matrículas"
                      );
                    } finally {
                      setExtractingMatriculas(false);
                    }
                  }}
                >
                  Re-extraer / actualizar planilla
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  href={
                    caseId
                      ? matriculaAPI.getXlsxUrl(parseInt(caseId))
                      : undefined
                  }
                  disabled={!matriculas.length}
                >
                  Descargar XLSX
                </Button>
              </Space>
            }
          >
            <MatriculaPlanillaTable
              rows={matriculas}
              loading={matriculasLoading}
              showCaseColumn={false}
              emptyText="Sin matrículas. Suba folios reales y pulse Re-extraer OCR."
              onMatriculaUpdated={(row) =>
                setMatriculas((prev) =>
                  prev.map((r: any) =>
                    r.matricula_id === row.matricula_id ? { ...r, ...row } : r
                  )
                )
              }
            />
          </Card>
        </TabPane>

        <TabPane tab="🔍 Pruebas" key="4">
          <Card>
            <Table
              dataSource={evidence}
              rowKey="evidence_id"
              columns={[
                {
                  title: "Tipo",
                  dataIndex: "evidence_type",
                  key: "evidence_type",
                },
                {
                  title: "Descripción",
                  dataIndex: "description",
                  key: "description",
                },
                {
                  title: "Fecha de Recolección",
                  dataIndex: "collection_date",
                  key: "collection_date",
                  render: (date: string) =>
                    date ? dayjs(date).format("DD/MM/YYYY") : "N/A",
                },
                {
                  title: "Estado",
                  dataIndex: "status",
                  key: "status",
                  render: (status: string) => <Tag>{status}</Tag>,
                },
                {
                  title: "Acciones",
                  key: "actions",
                  render: (_: any, record: any) => (
                    <Space>
                      {record.file_path ? (
                        <>
                          <a
                            href={evidenceAPI.getDownloadUrl(record.evidence_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Ver
                          </a>
                          <Button
                            type="link"
                            size="small"
                            icon={<RobotOutlined />}
                            onClick={async () => {
                              setAnalyzingDocument(record.evidence_id + 10000);
                              try {
                                const analysis = await evidenceAPI.analyze(
                                  record.evidence_id
                                );
                                setDocumentAnalysis({
                                  documentId: record.evidence_id,
                                  documentTitle: record.description || "Prueba",
                                  analysis,
                                });
                                setAnalysisModalVisible(true);
                                message.success("Análisis completado");
                              } catch (e: any) {
                                message.error(
                                  e?.response?.data?.detail || "Error al analizar"
                                );
                              } finally {
                                setAnalyzingDocument(null);
                              }
                            }}
                            loading={
                              analyzingDocument === record.evidence_id + 10000
                            }
                          >
                            Analizar
                          </Button>
                        </>
                      ) : (
                        <Upload
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tif,.tiff,.gif,.bmp,.mp3,.wav,.ogg,.opus,.m4a,.mp4,.webm,.mov,.avi,.mkv,audio/*,video/*,image/*"
                          showUploadList={false}
                          customRequest={async ({ file }) => {
                            try {
                              await evidenceAPI.uploadFile(
                                record.evidence_id,
                                file as File
                              );
                              message.success("Archivo adjuntado");
                              if (caseId) fetchCaseData(parseInt(caseId));
                            } catch {
                              message.error("Error al subir");
                            }
                          }}
                        >
                          <Button type="link" size="small" icon={<UploadOutlined />}>
                            Adjuntar archivo
                          </Button>
                        </Upload>
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        </TabPane>

        <TabPane tab="🤖 Análisis IA" key="5">
          <Card
            extra={
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={handleAIAnalysis}
              >
                Ejecutar Análisis
              </Button>
            }
          >
            {aiAnalysis ? (
              <div>
                <Card title="Análisis de Riesgo" style={{ marginBottom: 16 }}>
                  <pre>{JSON.stringify(aiAnalysis.risk, null, 2)}</pre>
                </Card>
                <Card title="Omisiones Detectadas" style={{ marginBottom: 16 }}>
                  <pre>{JSON.stringify(aiAnalysis.omissions, null, 2)}</pre>
                </Card>
                <Card title="Precedentes Relevantes">
                  <pre>{JSON.stringify(aiAnalysis.precedents, null, 2)}</pre>
                </Card>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <FileTextOutlined style={{ fontSize: "48px", color: "#ccc" }} />
                <p>Ejecuta el análisis de IA para ver insights automáticos</p>
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>

      <Modal
        title={
          documentAnalysis ? (
            <span>
              🤖 Asesoramiento Legal: {documentAnalysis.documentTitle}
              {documentAnalysis.analysis?.saved_at && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginLeft: "10px",
                  }}
                >
                  (Guardado:{" "}
                  {new Date(documentAnalysis.analysis.saved_at).toLocaleString(
                    "es-ES"
                  )}
                  )
                </span>
              )}
            </span>
          ) : (
            "Análisis de Documento"
          )
        }
        open={analysisModalVisible}
        onCancel={() => {
          setAnalysisModalVisible(false);
          setDocumentAnalysis(null);
        }}
        footer={[
          String(caseId) === "9" ? (
            <Button
              key="legal"
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() =>
                openLegalInformeWindow(
                  9,
                  documentAnalysis?.documentId && documentAnalysis.documentId > 0
                    ? documentAnalysis.documentId
                    : undefined
                )
              }
            >
              Ver / Imprimir informe jurídico
            </Button>
          ) : null,
          String(caseId) === "9" ? (
            <Button
              key="print-mats"
              icon={<PrinterOutlined />}
              onClick={async () => {
                try {
                  let report = documentAnalysis?.analysis?.reporte_compra_7_docs;
                  if (!report?.ok) {
                    message.loading({
                      content: "Preparando listado de matrículas...",
                      key: "mats-print",
                      duration: 0,
                    });
                    report = await documentAPI.generateBoughtPropertiesReport(9);
                    if (documentAnalysis) {
                      setDocumentAnalysis({
                        ...documentAnalysis,
                        analysis: {
                          ...documentAnalysis.analysis,
                          reporte_compra_7_docs: report,
                        },
                      });
                    }
                    message.destroy("mats-print");
                  }
                  openMatriculasPrintWindow(report);
                } catch (e: any) {
                  message.error(
                    e?.response?.data?.detail || "No se pudo abrir el listado"
                  );
                }
              }}
            >
              Anexo matrículas
            </Button>
          ) : null,
          String(caseId) === "9" ? (
            <Button
              key="docx"
              icon={<DownloadOutlined />}
              href={documentAPI.informePrecautelaDocxUrl(9)}
              target="_blank"
            >
              Descargar Word
            </Button>
          ) : null,
          String(caseId) === "9" ? (
            <Button
              key="xlsx"
              icon={<DownloadOutlined />}
              href={documentAPI.downloadBoughtPropertiesXlsxUrl(9)}
              target="_blank"
            >
              Excel matrículas
            </Button>
          ) : null,
          <Button
            key="close"
            onClick={() => {
              setAnalysisModalVisible(false);
              setDocumentAnalysis(null);
            }}
          >
            Cerrar
          </Button>,
        ]}
        width={900}
      >
        {documentAnalysis && documentAnalysis.analysis ? (
          <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {documentAnalysis.analysis.error ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <p style={{ color: "red", fontSize: "16px" }}>
                  {documentAnalysis.analysis.error}
                </p>
                {documentAnalysis.analysis.error_type === "quota_exceeded" && (
                  <p style={{ marginTop: "10px", color: "#666" }}>
                    Por favor, verifica tu configuración de OpenAI o contacta al
                    administrador.
                  </p>
                )}
              </div>
            ) : (
              <div>
                {String(caseId) === "9" &&
                  (documentAnalysis.analysis.formato === "informe_juridico" ||
                    documentAnalysis.analysis.informe_tipo ===
                      "legal_precautela" ||
                    documentAnalysis.analysis.informe_tipo ===
                      "legal_monitorio" ||
                    documentAnalysis.analysis.informe_tipo ===
                      "legal_auto_vista") && (
                  <Card
                    size="small"
                    style={{
                      marginBottom: 16,
                      background: "#f5f8f6",
                      borderColor: "#1F3D2B",
                    }}
                    title={
                      documentAnalysis.analysis.informe_tipo ===
                      "legal_auto_vista"
                        ? "Informe jurídico — Auto de Vista 194/2026 + 3 testimonios"
                        : documentAnalysis.analysis.informe_tipo ===
                          "legal_monitorio"
                        ? "Informe jurídico — Acta de Monitorio (Exp. 464/2025)"
                        : "Informe jurídico de precautela — Departamento legal"
                    }
                    extra={
                      <Space>
                        <Button
                          size="small"
                          type="primary"
                          icon={<FileTextOutlined />}
                          onClick={() =>
                            openLegalInformeWindow(
                              9,
                              documentAnalysis.documentId > 0
                                ? documentAnalysis.documentId
                                : undefined
                            )
                          }
                        >
                          Ver / Imprimir informe
                        </Button>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          href={
                            documentAnalysis.analysis.informe_tipo ===
                            "legal_auto_vista"
                              ? documentAPI.informeAutoVistaDocxUrl(
                                  9,
                                  documentAnalysis.documentId > 0
                                    ? documentAnalysis.documentId
                                    : undefined
                                )
                              : documentAnalysis.analysis.informe_tipo ===
                                "legal_monitorio"
                              ? documentAPI.informeMonitorioDocxUrl(
                                  9,
                                  documentAnalysis.documentId > 0
                                    ? documentAnalysis.documentId
                                    : undefined
                                )
                              : documentAPI.informePrecautelaDocxUrl(9)
                          }
                          target="_blank"
                        >
                          Word
                        </Button>
                        {documentAnalysis.analysis.informe_tipo !==
                          "legal_monitorio" &&
                          documentAnalysis.analysis.informe_tipo !==
                            "legal_auto_vista" && (
                          <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            href={documentAPI.downloadBoughtPropertiesXlsxUrl(9)}
                            target="_blank"
                          >
                            Excel
                          </Button>
                        )}
                      </Space>
                    }
                  >
                    <p style={{ marginBottom: 8 }}>
                      {documentAnalysis.analysis.informe_tipo ===
                      "legal_auto_vista"
                        ? "Informe tras Auto de Vista: dispositivo (confirma 12/03/2026; 194/2025 sin efecto), cronología y los 3 testimonios de Rossemary."
                        : documentAnalysis.analysis.informe_tipo ===
                          "legal_monitorio"
                        ? "Informe formal post-acta: resultado procesal, apelación, vínculo con frente notarial. Use Ver / Imprimir para PDF."
                        : "Informe formal (no OCR crudo): antecedentes, análisis, recomendaciones y anexo de matrículas. Use Ver / Imprimir informe para PDF / impresión."}
                    </p>
                    {(documentAnalysis.analysis.nivel_alerta ||
                      documentAnalysis.analysis.informe_tipo) && (
                      <p style={{ marginBottom: 0 }}>
                        Alerta:{" "}
                        <Tag
                          color={
                            documentAnalysis.analysis.informe_tipo ===
                              "legal_monitorio" ||
                            documentAnalysis.analysis.informe_tipo ===
                              "legal_auto_vista"
                              ? "green"
                              : "red"
                          }
                        >
                          {documentAnalysis.analysis.nivel_alerta || "ALTO"}
                        </Tag>
                        {documentAnalysis.analysis.titulo && (
                          <span style={{ marginLeft: 8 }}>
                            {documentAnalysis.analysis.titulo}
                          </span>
                        )}
                      </p>
                    )}
                  </Card>
                )}
                {documentAnalysis.analysis.formato === "informe_juridico" ||
                documentAnalysis.analysis.informe_tipo === "legal_precautela" ||
                documentAnalysis.analysis.informe_tipo === "legal_monitorio" ||
                documentAnalysis.analysis.informe_tipo === "legal_auto_vista" ? (
                  <div style={{ marginBottom: 16 }}>
                    <Descriptions bordered size="small" column={1}>
                      <Descriptions.Item label="Tipo">
                        {documentAnalysis.analysis.analisis_documento?.tipo ||
                          "Informe jurídico"}
                      </Descriptions.Item>
                      {documentAnalysis.analysis.aclaracion_dispositivo && (
                        <Descriptions.Item label="Aclaración dispositivo">
                          {documentAnalysis.analysis.aclaracion_dispositivo}
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Objeto del instrumento">
                        {documentAnalysis.analysis.analisis_documento
                          ?.objeto_del_instrumento ||
                          documentAnalysis.analysis.analisis_documento
                            ?.naturaleza ||
                          "—"}
                      </Descriptions.Item>
                      {documentAnalysis.analysis.analisis_documento
                        ?.resultado_auto && (
                        <Descriptions.Item label="Resultado Auto de Vista">
                          {Object.entries(
                            documentAnalysis.analysis.analisis_documento
                              .resultado_auto as Record<string, string>
                          )
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(" · ")}
                        </Descriptions.Item>
                      )}
                      {documentAnalysis.analysis.analisis_documento
                        ?.resultado_en_acta && (
                        <Descriptions.Item label="Resultado en acta">
                          {Object.entries(
                            documentAnalysis.analysis.analisis_documento
                              .resultado_en_acta as Record<string, string>
                          )
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(" · ")}
                        </Descriptions.Item>
                      )}
                      {(
                        documentAnalysis.analysis.cadena_testimonios_rossemary as
                          | Array<{ documento?: string; objeto?: string }>
                          | undefined
                      )?.length
                        ? (
                            <Descriptions.Item label="3 testimonios Rossemary">
                              {(
                                documentAnalysis.analysis
                                  .cadena_testimonios_rossemary as Array<{
                                  documento?: string;
                                  objeto?: string;
                                }>
                              )
                                .map(
                                  (t) =>
                                    `${t.documento || ""}: ${(t.objeto || "").slice(0, 120)}…`
                                )
                                .join(" | ")}
                            </Descriptions.Item>
                          )
                        : null}
                      {documentAnalysis.analysis.segundo_testimonio?.objeto &&
                        !documentAnalysis.analysis
                          .cadena_testimonios_rossemary && (
                        <Descriptions.Item label="Segundo testimonio 132/2025">
                          {documentAnalysis.analysis.segundo_testimonio.objeto}
                        </Descriptions.Item>
                      )}
                      {documentAnalysis.analysis.tercer_testimonio?.objeto &&
                        !documentAnalysis.analysis
                          .cadena_testimonios_rossemary && (
                        <Descriptions.Item label="Tercer testimonio 138/2025 (venta Lote 4)">
                          {documentAnalysis.analysis.tercer_testimonio.objeto}
                        </Descriptions.Item>
                      )}
                      {(documentAnalysis.analysis.analisis_documento
                        ?.comprador ||
                        documentAnalysis.analysis.tercer_testimonio
                          ?.comprador) && (
                        <Descriptions.Item label="Comprador (138/2025)">
                          {documentAnalysis.analysis.analisis_documento
                            ?.comprador ||
                            documentAnalysis.analysis.tercer_testimonio
                              ?.comprador}
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Problema central">
                        {documentAnalysis.analysis.analisis_documento
                          ?.problema_central || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Calificación">
                        {documentAnalysis.analysis.asesoramiento_legal
                          ?.calificacion_preliminar || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Prioridad">
                        {documentAnalysis.analysis.recomendaciones_especificas
                          ?.prioridad || "—"}
                      </Descriptions.Item>
                    </Descriptions>
                    <p style={{ marginTop: 12, color: "#666" }}>
                      El texto completo del informe jurídico está en la ventana
                      de impresión / Word. Aquí se omite el OCR crudo.
                    </p>
                  </div>
                ) : null}
                {!(
                  documentAnalysis.analysis.formato === "informe_juridico" ||
                  documentAnalysis.analysis.informe_tipo === "legal_precautela" ||
                  documentAnalysis.analysis.informe_tipo === "legal_monitorio" ||
                  documentAnalysis.analysis.informe_tipo === "legal_auto_vista"
                ) && (
                /* Helper function to render nested objects */
                (() => {
                  const renderValue = (
                    value: any,
                    depth: number = 0
                  ): React.ReactNode => {
                    if (value === null || value === undefined)
                      return <span style={{ color: "#999" }}>N/A</span>;

                    if (typeof value === "string") {
                      return <span>{value}</span>;
                    }

                    if (
                      typeof value === "number" ||
                      typeof value === "boolean"
                    ) {
                      return <span>{String(value)}</span>;
                    }

                    if (Array.isArray(value)) {
                      if (value.length === 0)
                        return <span style={{ color: "#999" }}>Ninguno</span>;
                      return (
                        <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                          {value.map((item, idx) => (
                            <li key={idx} style={{ marginBottom: "8px" }}>
                              {renderValue(item, depth + 1)}
                            </li>
                          ))}
                        </ul>
                      );
                    }

                    if (typeof value === "object") {
                      return (
                        <div style={{ marginLeft: depth > 0 ? "16px" : "0" }}>
                          {Object.entries(value).map(([key, val]) => (
                            <div key={key} style={{ marginBottom: "12px" }}>
                              <strong
                                style={{
                                  color: "#1890ff",
                                  display: "block",
                                  marginBottom: "4px",
                                }}
                              >
                                {key}:
                              </strong>
                              <div
                                style={{ marginLeft: "16px", color: "#333" }}
                              >
                                {renderValue(val, depth + 1)}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    return <span>{String(value)}</span>;
                  };

                  const analisisDoc =
                    documentAnalysis.analysis.analisis_documento ||
                    documentAnalysis.analysis["Análisis del Documento"];
                  const asesoramiento =
                    documentAnalysis.analysis.asesoramiento_legal ||
                    documentAnalysis.analysis["Asesoramiento Legal"];
                  const recomendaciones =
                    documentAnalysis.analysis.recomendaciones_especificas ||
                    documentAnalysis.analysis["Recomendaciones Específicas"];
                  const referencias =
                    documentAnalysis.analysis.referencias_legales ||
                    documentAnalysis.analysis["Referencias Legales"];

                  return (
                    <>
                      {analisisDoc && (
                        <Card
                          title={
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              📄 Análisis del Documento
                            </span>
                          }
                          style={{
                            marginBottom: 16,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div style={{ padding: "8px 0" }}>
                            {renderValue(analisisDoc)}
                          </div>
                        </Card>
                      )}

                      {asesoramiento && (
                        <Card
                          title={
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              ⚖️ Asesoramiento Legal
                            </span>
                          }
                          style={{
                            marginBottom: 16,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div style={{ padding: "8px 0" }}>
                            {renderValue(asesoramiento)}
                          </div>
                        </Card>
                      )}

                      {recomendaciones && (
                        <Card
                          title={
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              💡 Recomendaciones Específicas
                            </span>
                          }
                          style={{
                            marginBottom: 16,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div style={{ padding: "8px 0" }}>
                            {renderValue(recomendaciones)}
                          </div>
                        </Card>
                      )}

                      {referencias && (
                        <Card
                          title={
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              📚 Referencias Legales
                            </span>
                          }
                          style={{
                            marginBottom: 16,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div style={{ padding: "8px 0" }}>
                            {renderValue(referencias)}
                          </div>
                        </Card>
                      )}

                      {/* Fallback if no structured sections */}
                      {!analisisDoc &&
                        !asesoramiento &&
                        !recomendaciones &&
                        !referencias && (
                          <Card title="📄 Análisis Completo">
                            <div style={{ padding: "8px 0" }}>
                              {renderValue(documentAnalysis.analysis)}
                            </div>
                          </Card>
                        )}
                    </>
                  );
                })()
                )}
              </div>
            )}
          </div>
        ) : (
          <Spin />
        )}
      </Modal>
      <Modal
        title={`Contenido DB1995 — ${ocrViewTitle}`}
        open={ocrViewVisible}
        onCancel={() => setOcrViewVisible(false)}
        footer={null}
        width={900}
      >
        {ocrViewMeta ? (
          <p style={{ color: "#64748b", marginBottom: 12 }}>{ocrViewMeta}</p>
        ) : null}
        {ocrViewLoading ? (
          <Spin />
        ) : (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: "70vh",
              overflow: "auto",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              padding: 12,
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {ocrViewText}
          </pre>
        )}
      </Modal>
    </div>
  );
};

export default CaseView;
